import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LogIn, LogOut, FileText, Receipt, FolderKanban, AlertCircle, Bell, Download, Plus, ShieldCheck, ArrowUpRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { useSEO } from "@/components/common/SEO";
import { Reveal } from "@/components/common/Reveal";
import { SectionLabel } from "@/pages/Home";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function ClientArea() {
  const { lang, t } = useI18n();
  const navigate = useNavigate();
  const { user, checked, login, logout } = useAuth();
  useSEO({ lang, title: `${t("client.loginTitle")} | HO-SYS`, description: t("client.loginPrompt"), path: "/client" });

  const loggedIn = user && user.email;
  const isAdminOnClient = loggedIn && user.role === "admin";

  // If admin lands here through inline login or direct URL, redirect to admin panel
  useEffect(() => {
    if (checked && isAdminOnClient) {
      navigate(`/${lang}/admin`, { replace: true });
    }
  }, [checked, isAdminOnClient, lang, navigate]);

  return (
    <div className="min-h-screen pt-28 pb-20">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        {!checked || isAdminOnClient ? (
          <p className="text-white/50 text-center pt-20">{t("common.loading")}</p>
        ) : loggedIn ? (
          <Dashboard user={user} logout={logout} lang={lang} />
        ) : (
          <ClientLogin login={login} navigate={navigate} lang={lang} />
        )}
      </div>
    </div>
  );
}

function ClientLogin({ login, navigate, lang }) {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const inputCls = "w-full bg-[#0A0A0A] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-white/30 focus:outline-none focus:border-neon/60 transition-colors";

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await login(email, password);
      if (u?.role === "admin") navigate(`/${lang}/admin`, { replace: true });
    }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || t("login.error")); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto pt-8">
      <Reveal>
        <SectionLabel>{t("nav.clientArea")}</SectionLabel>
        <h1 className="mt-4 font-display text-3xl font-bold text-white">{t("client.loginTitle")}</h1>
        <p className="mt-3 text-white/55">{t("client.loginPrompt")}</p>
        <form onSubmit={submit} data-testid="client-login-form" className="mt-8 rounded-2xl border border-white/10 bg-white/[0.02] p-7 space-y-4">
          <input data-testid="client-login-email" type="email" required placeholder={t("login.email")} value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
          <input data-testid="client-login-password" type="password" required placeholder={t("login.password")} value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
          {error && <p data-testid="client-login-error" className="text-sm text-red-400">{error}</p>}
          <button data-testid="client-login-submit" type="submit" disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-neon px-6 py-3.5 font-semibold text-black hover:bg-white transition-colors disabled:opacity-60">
            {loading ? t("common.loading") : t("login.submit")} <LogIn className="w-4 h-4" />
          </button>
          <Link to={`/${lang}/login`} data-testid="client-forgot-link"
            className="block text-center text-xs text-white/50 hover:text-neon transition-colors pt-1">
            {lang === "es" ? "¿No recuerdas la contraseña?" : lang === "en" ? "Forgot your password?" : lang === "fr" ? "Mot de passe oublié ?" : "No recordo la contrasenya"}
          </Link>
        </form>
      </Reveal>
    </div>
  );
}

function Dashboard({ user, logout, lang }) {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [inc, setInc] = useState({ title: "", description: "", priority: "mitjana" });
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/client/overview").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submitIncident = async (e) => {
    e.preventDefault();
    if (!inc.title.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/client/incidents", inc);
      toast.success("Incidència creada");
      setInc({ title: "", description: "", priority: "mitjana" });
      load();
    } catch { toast.error("Error"); } finally { setSubmitting(false); }
  };

  const inputCls = "w-full bg-[#0A0A0A] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-neon/60";

  return (
    <div>
      {user.role === "admin" && (
        <div className="mb-6 rounded-2xl border border-neon/30 bg-neon/[0.06] p-4 sm:p-5 flex flex-wrap items-center justify-between gap-3" data-testid="admin-banner">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-neon shrink-0" />
            <div>
              <p className="text-white font-medium text-sm">Estàs autenticat com a administrador</p>
              <p className="text-white/60 text-xs mt-0.5">Aquesta és la vista de client. Per gestionar leads, blog i correus, ves al panell d&apos;admin.</p>
            </div>
          </div>
          <Link to={`/${lang}/admin`} data-testid="go-to-admin-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-neon px-5 py-2 text-sm font-semibold text-black hover:bg-white transition-colors">
            Panell d&apos;admin <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      )}

      <div className="flex items-center justify-between mb-8">
        <div>
          <SectionLabel>{t("nav.clientArea")}</SectionLabel>
          <h1 className="mt-3 font-display text-3xl font-bold text-white">{t("client.welcome")}, {user.name}</h1>
        </div>
        <button data-testid="client-logout" onClick={logout}
          className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-white/80 hover:text-white hover:border-red-400/50 transition-colors">
          <LogOut className="w-4 h-4" /> {t("client.logout")}
        </button>
      </div>

      {data?.notifications?.length > 0 && (
        <div className="mb-6 rounded-2xl border border-neon/25 bg-neon/[0.04] p-5">
          <div className="flex items-center gap-2 mb-3"><Bell className="w-4 h-4 text-neon" /><h3 className="font-display font-semibold text-white">{t("client.notifications")}</h3></div>
          <ul className="space-y-1.5">
            {data.notifications.map((n) => <li key={n.id} className="text-sm text-white/60 flex justify-between"><span>{n.text}</span><span className="text-white/30">{n.date}</span></li>)}
          </ul>
        </div>
      )}

      <Tabs defaultValue="projects">
        <TabsList className="bg-[#0A0A0A] border border-white/10 flex-wrap h-auto">
          <TabsTrigger value="projects" data-testid="client-tab-projects"><FolderKanban className="w-4 h-4 mr-1.5" />{t("client.projects")}</TabsTrigger>
          <TabsTrigger value="documents" data-testid="client-tab-documents"><FileText className="w-4 h-4 mr-1.5" />{t("client.documents")}</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="client-tab-invoices"><Receipt className="w-4 h-4 mr-1.5" />{t("client.invoices")}</TabsTrigger>
          <TabsTrigger value="incidents" data-testid="client-tab-incidents"><AlertCircle className="w-4 h-4 mr-1.5" />{t("client.incidents")}</TabsTrigger>
        </TabsList>

        <TabsContent value="projects" className="mt-6 space-y-3">
          {(data?.projects || []).map((p) => (
            <div key={p.id} className="rounded-xl border border-white/10 bg-[#0A0A0A] p-5">
              <div className="flex items-center justify-between">
                <h3 className="font-display font-semibold text-white">{p.name}</h3>
                <span className="rounded-full bg-white/5 px-3 py-1 text-xs text-white/60">{p.status}</span>
              </div>
              <div className="mt-3 h-2 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full bg-neon rounded-full" style={{ width: `${p.progress}%` }} />
              </div>
              <div className="mt-1.5 flex justify-between text-xs text-white/40"><span>{t("client.progress")}: {p.progress}%</span><span>{p.updated}</span></div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="documents" className="mt-6 space-y-3">
          {(data?.documents || []).map((d) => (
            <div key={d.id} className="flex items-center justify-between rounded-xl border border-white/10 bg-[#0A0A0A] p-4">
              <div className="flex items-center gap-3"><FileText className="w-5 h-5 text-neon" /><div><p className="text-white text-sm font-medium">{d.name}</p><span className="text-xs text-white/40">{d.type} · {d.size} · {d.date}</span></div></div>
              <button onClick={() => toast.success("Descàrrega disponible aviat")} className="p-2 text-white/50 hover:text-neon transition-colors"><Download className="w-4 h-4" /></button>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="invoices" className="mt-6 space-y-3">
          {(data?.invoices || []).map((f) => (
            <div key={f.id} className="flex items-center justify-between rounded-xl border border-white/10 bg-[#0A0A0A] p-4">
              <div><p className="text-white text-sm font-medium">{f.number}</p><span className="text-xs text-white/40">{f.date}</span></div>
              <div className="flex items-center gap-4">
                <span className="text-white font-display font-semibold">{f.amount}</span>
                <span className={`rounded-full px-3 py-1 text-xs ${f.status === "Pagada" ? "bg-lime/15 text-lime border border-lime/30" : "bg-orange-500/15 text-orange-300 border border-orange-400/30"}`}>
                  {f.status === "Pagada" ? t("client.paid") : t("client.pending")}
                </span>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="incidents" className="mt-6">
          <form onSubmit={submitIncident} data-testid="client-incident-form" className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 mb-6">
            <div className="flex items-center gap-2 mb-4"><Plus className="w-4 h-4 text-neon" /><h3 className="font-display font-semibold text-white">{t("client.newIncident")}</h3></div>
            <div className="space-y-3">
              <input data-testid="incident-title" value={inc.title} onChange={(e) => setInc({ ...inc, title: e.target.value })} placeholder={t("client.incidentTitle")} className={inputCls} />
              <textarea data-testid="incident-desc" rows={3} value={inc.description} onChange={(e) => setInc({ ...inc, description: e.target.value })} placeholder={t("client.incidentDesc")} className={inputCls + " resize-none"} />
              <select data-testid="incident-priority" value={inc.priority} onChange={(e) => setInc({ ...inc, priority: e.target.value })} className={inputCls}>
                <option value="alta">{t("client.priorities.alta")}</option>
                <option value="mitjana">{t("client.priorities.mitjana")}</option>
                <option value="baixa">{t("client.priorities.baixa")}</option>
              </select>
            </div>
            <button data-testid="incident-submit" type="submit" disabled={submitting} className="mt-4 inline-flex items-center gap-2 rounded-full bg-neon px-6 py-3 font-semibold text-black hover:bg-white transition-colors disabled:opacity-60">
              {t("client.submit")}
            </button>
          </form>
          <div className="space-y-3">
            {(data?.incidents || []).map((i) => (
              <div key={i.id} data-testid={`incident-${i.id}`} className="rounded-xl border border-white/10 bg-[#0A0A0A] p-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-white font-medium">{i.title}</h4>
                  <span className={`rounded-full px-3 py-1 text-xs ${i.status === "resolta" ? "bg-lime/15 text-lime border border-lime/30" : "bg-white/5 text-white/60"}`}>{i.status}</span>
                </div>
                <p className="mt-1.5 text-sm text-white/50">{i.description}</p>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
